import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { BellRing } from 'lucide-react'
import { PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import {
  PolicyOrderGroup,
  policySelectorSummary,
  PRINTER_SELECTOR_FIELDS,
  type PolicyKindContribution,
  type PolicyListGroupProps,
} from '@/modules/hierarchy'
import { Dialog, DialogContent } from '@/ui/dialog'
import {
  eventRulePoliciesApi,
  PRINTER_EVENT_RULE_POLICY_TYPE,
  type PrinterEventRulePolicy,
} from '../../api/event-rule-policies-api'
import { EventRulePolicyForm } from './event-rule-policy-form'
import { useDeleteEventRulePolicy } from './event-rule-policy-queries'
import { usePrinterPolicyGroup } from '../printer-policies/use-printer-policy-group'
import { PrinterPolicyDeleteDialog } from '../printer-policies/printer-policy-delete-dialog'
import { usePrinterPolicyReconciliation } from '../printer-policies/use-printer-policy-reconciliation'

export const printerEventRulePolicyKind: PolicyKindContribution = {
  valueType: PRINTER_EVENT_RULE_POLICY_TYPE,
  label: 'Event Rule',
  description:
    'Define an event condition and the actions to run when it matches.',
  icon: BellRing,
  order: 40,
  readPermission: PERMISSION_KEYS.alertRead,
  managePermission: PERMISSION_KEYS.alertManage,
  create: ({ entityId, onClose, onCreateLockChange }) => (
    <EventRulePolicyForm entityId={entityId} policy={null} onClose={onClose} onCreateLockChange={onCreateLockChange} />
  ),
  list: EventRulePolicyList,
}

function EventRulePolicyList({
  entityId,
  policies,
  complete,
  canReadHistory = false,
  onAudit,
  returnFocus,
  grouped,
  onCreate,
  selection,
  resolvedPolicy,
}: PolicyListGroupProps) {
  const queryClient = useQueryClient()
  const { can } = usePermissionsAt(entityId)
  const canEdit = can(PERMISSION_KEYS.alertManage)
  const selectedId = selection?.policyId
  const group = usePrinterPolicyGroup(entityId, PRINTER_EVENT_RULE_POLICY_TYPE, Boolean(grouped) && can(PERMISSION_KEYS.alertRead),
    policies.some((policy) => policy.id === selectedId) || resolvedPolicy?.id === selectedId ? selectedId : undefined)
  const listElement = useRef<HTMLElement | null>(null)
  const actionTrigger = useRef<HTMLButtonElement | null>(null)
  const remove = useDeleteEventRulePolicy(entityId)
  const deletion = usePrinterPolicyReconciliation(entityId, PRINTER_EVENT_RULE_POLICY_TYPE)
  const [editing, setEditing] = useState<PrinterEventRulePolicy | null>(null)
  const [deleting, setDeleting] = useState<PrinterEventRulePolicy | null>(null)
  const [editorVersion, setEditorVersion] = useState(0)
  const [reloading, setReloading] = useState(false)
  const [editorPending, setEditorPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function load(policyId: string, operation: 'edit' | 'delete') {
    if (!canEdit) return
    setReloading(true)
    setError(null)
    try {
      const detail = await eventRulePoliciesApi.get(entityId, policyId)
      if (operation === 'delete') {
        remove.reset()
        setDeleting(detail)
      } else {
        setEditing(detail)
        setEditorVersion((version) => version + 1)
      }
    } catch {
      setError('The event rule could not be loaded. Try again.')
    } finally {
      setReloading(false)
    }
  }
  function finalFocus() {
    if (actionTrigger.current?.isConnected) return actionTrigger.current
    return listElement.current?.querySelector<HTMLButtonElement>('h2 button') ?? returnFocus?.() ?? listElement.current ?? null
  }

  return (
    <section ref={listElement} tabIndex={-1} className="flex min-w-0 flex-col gap-6" aria-label="Policies">
      {error && !editing && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <PolicyOrderGroup
        grouped={grouped}
        selection={selection}
        resolvedPolicy={resolvedPolicy}
        onCreate={onCreate}
        expanded={group.expanded}
        onExpandedChange={group.setExpanded}
        selectorSummary={(policy) => policySelectorSummary(policy.selectorExpression, policy.selectorGui, PRINTER_SELECTOR_FIELDS, 'All printers in scope')}
        entityId={entityId}
        valueType={PRINTER_EVENT_RULE_POLICY_TYPE}
        policies={policies}
        complete={complete}
        label="Printer event rules"
        composition="additive"
        canEdit={canEdit}
        canReadHistory={canReadHistory}
        onAudit={onAudit}
        reloading={reloading}
        onEdit={(policy, trigger) => { actionTrigger.current = trigger; void load(policy.id, 'edit') }}
        onDelete={(policy, trigger) => { actionTrigger.current = trigger; void load(policy.id, 'delete') }}
      />
      <Dialog
        open={editing !== null && canEdit}
        onOpenChange={(open) => {
          if (open || editorPending || reloading
            || queryClient.isMutating({ mutationKey: ['printer-policy-save', entityId, PRINTER_EVENT_RULE_POLICY_TYPE] }) > 0) return
          setEditing(null)
        }}
      >
        <DialogContent finalFocus={finalFocus} className="max-h-[90dvh] max-w-3xl overflow-y-auto has-[[data-slot=printer-policy-conflict-review]]:max-w-6xl">
          {editing && canEdit && (
            <EventRulePolicyForm
              key={`${editing.policyId}:${editorVersion}`}
              entityId={entityId}
              policy={editing}
              onClose={() => setEditing(null)}
              onPendingChange={setEditorPending}
              onReload={() => void load(editing.policyId, 'edit')}
              reloading={reloading}
              reloadError={error}
            />
          )}
        </DialogContent>
      </Dialog>
      {deleting && <PrinterPolicyDeleteDialog
        finalFocus={finalFocus}
        name={deleting.name}
        canDelete={canEdit}
        onClose={() => setDeleting(null)}
        onReload={deletion.reload}
        description="This rule will stop applying to printers. Other matching event rules remain in place."
        mutation={remove}
        onConfirm={() => {
          if (canEdit && !remove.isPending)
            remove.mutate(deleting, {
              onSuccess: () => setDeleting(null),
            })
        }}
      />}
    </section>
  )
}
